import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/Shared';
import { money, num } from '@/lib/format';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Boxes, DollarSign, TrendingUp, Wallet, ClipboardList, AlertTriangle, PiggyBank, Receipt
} from 'lucide-react';
import {
  RevenueTrendCard, TopSellingCard, LowStockCard, EmployeeSummaryCard, RecentSalesCard, RecentPurchasesCard,
} from '@/components/DashboardWidgets';

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}><Icon className="h-[18px] w-[18px]" /></span>
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-semibold tabular-nums tracking-tight font-heading">{value}</div>
    </Card>
  );
}

const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

function kpiConfig(k) {
  return [
    { icon: Boxes, label: 'Inventory Units', value: num(k.total_inventory_units), accent: 'bg-teal-50 text-teal-700', testid: 'dashboard-kpi-total-inventory-units' },
    { icon: Wallet, label: 'Inventory Value', value: money(k.inventory_value), accent: 'bg-blue-50 text-blue-700', testid: 'dashboard-kpi-inventory-value' },
    { icon: DollarSign, label: 'Total Revenue', value: money(k.total_revenue), accent: 'bg-emerald-50 text-emerald-700', testid: 'dashboard-kpi-total-revenue' },
    { icon: Receipt, label: 'Total Cost (COGS)', value: money(k.total_cost), accent: 'bg-amber-50 text-amber-700', testid: 'dashboard-kpi-total-cost' },
    { icon: TrendingUp, label: 'Gross Profit', value: money(k.gross_profit), accent: 'bg-emerald-50 text-emerald-700', testid: 'dashboard-kpi-gross-profit' },
    { icon: PiggyBank, label: 'Net Profit', value: money(k.net_profit), accent: 'bg-teal-50 text-teal-700', testid: 'dashboard-kpi-net-profit' },
    { icon: ClipboardList, label: 'Pending Requests', value: num(k.pending_requests), accent: 'bg-amber-50 text-amber-700', testid: 'dashboard-kpi-pending-requests' },
    { icon: AlertTriangle, label: 'Low Stock Items', value: num(k.low_stock_count), accent: 'bg-red-50 text-red-700', testid: 'dashboard-kpi-low-stock' },
  ];
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get('/dashboard').then((res) => setData(res.data)).catch((err) => { if (process.env.NODE_ENV !== 'production') console.error('Dashboard load failed:', err); }); }, []);

  if (!data) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {SKELETON_KEYS.map((k) => <Skeleton key={`kpi-skel-${k}`} className="h-28 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Business overview for Lailany Inc Warehouse" testid="dashboard-header" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiConfig(data.kpis).map((kpi) => <Kpi key={kpi.testid} {...kpi} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <RevenueTrendCard data={data.revenue_trend} />
        <TopSellingCard data={data.top_selling} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <LowStockCard items={data.low_stock} />
        <EmployeeSummaryCard items={data.employee_summary} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <RecentSalesCard items={data.recent_sales} />
        <RecentPurchasesCard items={data.recent_purchases} />
      </div>
    </div>
  );
}
